import { useState, useCallback, useMemo } from "react";
import { Text, Flex, Code, Button, Spinner, Checkbox } from "@radix-ui/themes";
import { useApi } from "@/api/useApi";
import { useQuery, useMutation } from "react-query";
import { toast } from "sonner";
import MultiSelect, {
  SelectOption,
} from "@/components/shared/multi-select/Select";
import { useExploreAggInteractions } from "@/v2/entities/explore/Explore.api";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";

type ReclassifyTopicsPayload = {
  topicIds: string[];
  deleteTopics: boolean;
};

interface ReclassifyTopicsProps {
  onClose: () => void;
}

export function ReclassifyTopics({ onClose }: ReclassifyTopicsProps) {
  const { api } = useApi();

  const [topicIds, setTopicIds] = useState<string[]>([]);
  const [deleteTopic, setDeleteTopic] = useState(false);
  const { workspace } = useWorkspace();

  const { mutate: reclassifyTopics, isLoading: isReclassifyingTopics } =
    useMutation({
      mutationFn: async (payload: ReclassifyTopicsPayload) => {
        const response = await api.post(
          `platform/admin-dashboard/tenants/${workspace}/reassign-topics`,
          payload
        );
        return response.data;
      },
      onSuccess: () => {
        toast.success("Topics reclassified successfully");
        onClose();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Failed to reclassify topics"
        );
      },
    });

  const handleSubmit = useCallback(() => {
    if (topicIds.length === 0) {
      toast.error("Please select at least one topic");
      return;
    }

    reclassifyTopics({
      topicIds,
      deleteTopics: deleteTopic,
    });
  }, [topicIds, deleteTopic, reclassifyTopics]);

  const exploreAggInteractionsQuery = useExploreAggInteractions({
    source: "tickets",
    groupBy: "topic",
    sorting: [],
  });

  const topics = useMemo<SelectOption[]>(() => {
    const options: SelectOption[] = [];

    exploreAggInteractionsQuery.data?.results.forEach((result) => {
      if (result.topic && result.id && result.volume > 0) {
        options.push({
          label: `${result.topic} (${result.volume})`,
          value: result.id,
        });
      }
    });

    return options;
  }, [exploreAggInteractionsQuery.data]);

  const totalTickets = useMemo(() => {
    if (!exploreAggInteractionsQuery.data?.results || topicIds.length === 0) {
      return 0;
    }

    return exploreAggInteractionsQuery.data.results
      .filter((result) => topicIds.includes(result.id ?? ""))
      .reduce((sum, result) => sum + result.volume, 0);
  }, [exploreAggInteractionsQuery.data?.results, topicIds]);

  const isSubmitDisabled = topicIds.length === 0;

  return (
    <Flex p={"4"} direction="column" gap="4">
      {/* Source Section */}
      <Flex direction="column" gap="4">
        <Text size="4" weight="bold" color="gray">
          Topics
        </Text>
        <MultiSelect
          placeholder="Select topics"
          options={topics}
          value={topicIds}
          onChange={(value: string[]) => {
            setTopicIds(value);
          }}
        />
        {topicIds.length > 0 ? (
          <Text size="2" color="gray">
            Total tickets: {totalTickets}
          </Text>
        ) : (
          <div style={{ height: "20px" }}></div>
        )}
      </Flex>

      {/* Delete Topic Checkbox */}
      <Flex align="center" gap="2">
        <Checkbox
          checked={deleteTopic}
          onCheckedChange={(checked) => setDeleteTopic(checked as boolean)}
        />
        <Text size="2" color="gray">
          Delete Topics
        </Text>
      </Flex>

      {/* Reclassify Button */}
      <Button
        size="3"
        color="yellow"
        variant="solid"
        onClick={handleSubmit}
        disabled={isSubmitDisabled || isReclassifyingTopics}
      >
        <Spinner loading={isReclassifyingTopics} />
        Reclassify
      </Button>
    </Flex>
  );
}
